import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { enqueueSnackbar } from "notistack";
import { useEffect, useState } from "react";
import {
  useGenerateTicketsMutation,
  usePaymentCommitTransactionMutation,
  usePaymentInitTransactionMutation,
} from "../../store/api";
import { useAppSelector } from "../../store/hooks/useAppSelector";
import { RootState } from "../../store/types/rootState";
import { PHONE_PREFIXES, getPhonePrefix } from "../../constants/phonePrefixes";
import {
  buildSelfVerificationUrl,
  buildTicketsWhatsappMessage,
  buildWhatsappUrl,
  toWhatsappPhone,
} from "../../utils/whatsapp";

export type TalonarioSaleMode = "apartar" | "vender";

interface TalonarioSaleDialogProps {
  open: boolean;
  mode: TalonarioSaleMode;
  raffleId: string;
  raffleName: string;
  selectedTickets: number[];
  formatTicket: (ticket: number) => string;
  onClose: (completed: boolean) => void;
}

interface CompletedSale {
  name: string;
  email: string;
  phone: string;
}

const EMPTY_PURCHASER = { name: "", email: "", phone: "" };

export const TalonarioSaleDialog = ({
  open,
  mode,
  raffleId,
  raffleName,
  selectedTickets,
  formatTicket,
  onClose,
}: TalonarioSaleDialogProps) => {
  const account = useAppSelector(({ account }: RootState) => account);
  const supportWhatsapp = account.socialMediaList.find(sm => sm.name === "supportWhatsapp")?.username;

  const [purchaser, setPurchaser] = useState(EMPTY_PURCHASER);
  const [phonePrefix, setPhonePrefix] = useState(getPhonePrefix(supportWhatsapp));
  const [password, setPassword] = useState("");
  const [completedSale, setCompletedSale] = useState<CompletedSale | undefined>();

  const [initTransaction, { isLoading: isLoadingInit }] = usePaymentInitTransactionMutation();
  const [commitTransaction, { isLoading: isLoadingCommit }] = usePaymentCommitTransactionMutation();
  const [generateTickets, { isLoading: isLoadingGenerate }] = useGenerateTicketsMutation();
  const isSubmitting = isLoadingInit || isLoadingCommit || isLoadingGenerate;

  const isSale = mode === "vender";
  const actionLabel = isSale ? "Vender" : "Apartar";
  const ticketLabels = selectedTickets.map(formatTicket);

  useEffect(() => {
    if (open) {
      setPurchaser(EMPTY_PURCHASER);
      setPassword("");
      setCompletedSale(undefined);
      setPhonePrefix(getPhonePrefix(supportWhatsapp));
    }
  }, [open, supportWhatsapp]);

  const showError = (message: string) => {
    enqueueSnackbar(message, {
      variant: "error",
      anchorOrigin: { horizontal: "right", vertical: "top" },
    });
  };

  // Venta: la orden ya quedó autorizada con la contraseña; falta confirmar el pago y generar los tickets
  const confirmCashSale = async (purchaseOrderId: string) => {
    const commitResponse = await commitTransaction({
      raffleId,
      conf: { paymentMethodId: "cash", orderId: purchaseOrderId },
    }).unwrap();

    if (commitResponse.status === "FAILED") {
      showError("No se pudo confirmar el pago.");
      return false;
    }

    const generateResponse = await generateTickets({
      raffleId,
      config: { purchaseOrderId, accountId: account.id },
    }).unwrap();

    if (generateResponse.status === "FAILED") {
      showError("El pago se confirmó pero no se pudieron generar los tickets. Revísalo en Verificación.");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    const name = purchaser.name.trim();
    const email = purchaser.email.trim().toLowerCase();
    const localPhone = purchaser.phone.replace(/\D/g, "").replace(/^0+/, "");

    if (!name || !email || !localPhone) {
      showError("Ingrese nombre, email y teléfono.");
      return;
    }
    if (isSale && !password) {
      showError("Ingrese la contraseña para vender.");
      return;
    }

    const phone = `${phonePrefix}${localPhone}`;

    try {
      const initResponse = await initTransaction({
        raffleId,
        conf: {
          ticketQuantity: selectedTickets.length,
          selectedTickets,
          paymentMethodId: isSale ? "cash" : "transfer",
          passwordCash: isSale ? password : undefined,
          purchaser: { name, email, phone },
          targetBankAccount: isSale
            ? { id: "cash", name: "cash", accountNumber: "cash" }
            : { id: "talonario", name: "Apartado por admin", accountNumber: "-" },
        },
      }).unwrap();

      const expectedStatus = isSale ? "AUTHORIZED_FOR_GENERATE_TICKETS" : "TRANSFER_PAYMENT_CREATED";
      if (initResponse.status !== expectedStatus) {
        showError(initResponse.error || "No se pudo crear la orden.");
        return;
      }

      if (isSale && !(await confirmCashSale(initResponse.purchaseOrderId))) {
        return;
      }

      setCompletedSale({ name, email, phone });
    } catch {
      showError(isSale ? "Error al vender los números." : "Error al apartar los números.");
    }
  };

  const handleSendWhatsapp = () => {
    if (!completedSale) return;
    const message = buildTicketsWhatsappMessage({
      purchaserName: completedSale.name,
      raffleName,
      tickets: ticketLabels,
      isPaid: isSale,
      link: buildSelfVerificationUrl(account.url, completedSale.email, raffleId),
    });
    window.open(buildWhatsappUrl(toWhatsappPhone(completedSale.phone, phonePrefix), message), "_blank");
  };

  return (
    <Dialog open={open} onClose={() => !isSubmitting && onClose(!!completedSale)} fullWidth maxWidth="sm">
      <DialogTitle>{isSale ? "Vender números" : "Apartar números"}</DialogTitle>
      <DialogContent>
        {completedSale ? (
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <Alert severity="success">
              Números {ticketLabels.join(", ")} {isSale ? "vendidos" : "apartados"} a {completedSale.name}.
            </Alert>
            <Button
              variant="contained"
              color="success"
              startIcon={<WhatsAppIcon />}
              onClick={handleSendWhatsapp}
              fullWidth
            >
              Enviar por WhatsApp
            </Button>
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <Typography variant="body2">
              Números seleccionados: <strong>{ticketLabels.join(", ")}</strong>
            </Typography>
            <TextField
              label="Nombre"
              fullWidth
              value={purchaser.name}
              onChange={(e) => setPurchaser({ ...purchaser, name: e.target.value })}
            />
            <TextField
              label="Email"
              type="email"
              fullWidth
              value={purchaser.email}
              onChange={(e) => setPurchaser({ ...purchaser, email: e.target.value })}
            />
            <Stack direction="row" spacing={1}>
              <Select value={phonePrefix} onChange={(e) => setPhonePrefix(e.target.value)} sx={{ minWidth: 110 }}>
                {PHONE_PREFIXES.map((prefix) => (
                  <MenuItem key={prefix.code} value={prefix.code}>
                    {prefix.flag} {prefix.code}
                  </MenuItem>
                ))}
              </Select>
              <TextField
                label="Teléfono (WhatsApp)"
                fullWidth
                inputProps={{ inputMode: "tel" }}
                value={purchaser.phone}
                onChange={(e) => setPurchaser({ ...purchaser, phone: e.target.value })}
              />
            </Stack>
            {isSale && (
              <TextField
                label="Contraseña"
                type="password"
                fullWidth
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            )}
            {!isSale && (
              <Alert severity="info">
                Los números quedan apartados hasta que la persona adjunte su comprobante y se valide el pago.
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {completedSale ? (
          <Button onClick={() => onClose(true)}>Cerrar</Button>
        ) : (
          <>
            <Button onClick={() => onClose(false)} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button onClick={handleSubmit} variant="contained" disabled={isSubmitting}>
              {isSubmitting ? <CircularProgress size={22} /> : actionLabel}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
